import { call, put, takeLatest } from "redux-saga/effects";
import axios, { AxiosResponse } from "axios";
import handRequest from "@util/net/hand";
import { executeOnceAtATime } from "@util/net/index";
import {
  FavoritesItemActions,
  GET_FAVORITES_ITEM,
  POST_FAVORITES_ITEM,
  DELETE_FAVORITES_ITEM,
  ConfigActions
} from "@redux/reducer";
import {
  BRAND_LIST,
  COUNTRY,
  DelFavoritesItemPayload,
  FavoritesItemResponse,
  PostFavoritesItemPayload
} from "@type/index";
import { getToken } from "@util/webStorage";
import store from "@redux/store";
import { isSitOrUat } from "@util/country";
import {
  trackAddToWishListEvent,
  trackRemoveFromWishListEvent
} from "@util/analytics/utils";
import { isNotEmptyObj } from "@util/helpers";

// get favorites item list
export function* getFavoritesItemSaga() {
  try {
    yield put(FavoritesItemActions.getFavoritesItemLoading(true));
    const { region, brand_name, language } = store.getState().config;
    // GUHK
    if (brand_name === BRAND_LIST.GU && region === COUNTRY.HK) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        "/wishlist/favorite/list",
        {},
        {
          headers: {
            authorization: `Bearer ${getToken()}`
          }
        }
      );
      yield put(
        FavoritesItemActions.getFavoritesItemSuccess({
          data: res.data.resp,
          region,
          brand: brand_name
        })
      );
    }
    // UQHK -> new STG&PRD
    else if (
      brand_name === BRAND_LIST.UQ &&
      region === COUNTRY.HK &&
      !isSitOrUat()
    ) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        "/crm/wishlist/favorite/list",
        {},
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
            langCode: language
          }
        }
      );
      yield put(
        FavoritesItemActions.getFavoritesItemSuccess({
          data: res.data.resp,
          region,
          brand: brand_name
        })
      );
    }
    // GUTW & UQTW & UQHK(SIT&UAT)
    else {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        `/h/hmall-favorite-service/customer/favorite/list/${language}`,
        {},
        {
          headers: {
            authorization: `Bearer ${getToken()}`
          }
        }
      );
      yield put(
        FavoritesItemActions.getFavoritesItemSuccess({
          data: res.data.resp,
          region,
          brand: brand_name
        })
      );
    }
  } catch (error) {
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
  }
}

// add favorites item
export function* postFavoritesItemSaga(action: {
  payload: PostFavoritesItemPayload;
}) {
  try {
    const payload: PostFavoritesItemPayload = action.payload;
    yield put(FavoritesItemActions.getFavoritesItemLoading(true));
    const { region, brand_name, language } = store.getState().config;
    // add_to_wishlist GA event
    const addWishGaEvent = () => {
      const _price = isNotEmptyObj(payload.productInfo?.price)
        ? payload.productInfo?.price?.maxPrice ?? 0
        : 0;
      trackAddToWishListEvent({
        brand: brand_name as BRAND_LIST,
        country: region as COUNTRY,
        productData: {
          ...payload.productInfo,
          displayPrice: Number(_price) ?? 0,
          currency: region === COUNTRY.TW ? "TWD" : "HKD",
          wishColorName: payload.productInfo?.color?.name || "",
          wishColorCode: payload.productInfo?.color?.displayCode || "",
          wishSize: payload.productInfo?.size || "",
          wishSizeCode: "",
          imageUrl: payload.productInfo?.pdpImageUrl || "",
          index: payload.productInfo?.index || 0
        }
      });
    };
    // UQTW & GUTW
    if (region === COUNTRY.TW) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        `/h/hmall-favorite-service/customer/favorite/insert/${language}`,
        payload.itemArr[0],
        {
          headers: {
            authorization: `Bearer ${getToken()}`
          }
        }
      );
      if (res.data.success) {
        addWishGaEvent();
        yield put(
          FavoritesItemActions.postFavoritesItemSuccess(
            payload.itemArr[0].productCode
          )
        );
      } else {
        store.dispatch(ConfigActions.setShowWishIconError(true));
        if (res.data.msgCode === "UR_FAVORITE_01") {
          store.dispatch(ConfigActions.setWishMaxError(true));
        }
      }
    }
    // GUHK
    else if (brand_name === BRAND_LIST.GU) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        "/wishlist/favorite/insert",
        {
          productList: payload.itemArr
        },
        {
          headers: {
            authorization: `Bearer ${getToken()}`
          }
        }
      );
      if (res.data.success) {
        addWishGaEvent();
        yield put(
          FavoritesItemActions.postFavoritesItemSuccess(
            payload.itemArr[0].productCode
          )
        );
      } else {
        store.dispatch(ConfigActions.setShowWishIconError(true));
        if (res.data.msgCode === "UR_FAVORITE_01") {
          store.dispatch(ConfigActions.setWishMaxError(true));
        }
      }
    }
    // UQHK -> new STG&PRD
    else if (
      brand_name === BRAND_LIST.UQ &&
      region === COUNTRY.HK &&
      !isSitOrUat()
    ) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        "/crm/wishlist/favorite/insert",
        {
          productList: payload.itemArr
        },
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
            langCode: language
          }
        }
      );
      if (res.data.success) {
        addWishGaEvent();
        yield put(
          FavoritesItemActions.postFavoritesItemSuccess(
            payload.itemArr[0].productCode
          )
        );
      } else {
        store.dispatch(ConfigActions.setShowWishIconError(true));
        if (res.data.msgCode === "UR_FAVORITE_01") {
          store.dispatch(ConfigActions.setWishMaxError(true));
        }
      }
    }
    // UQHK(SIT&UAT)
    else {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        `/h/hmall-favorite-service/customer/favorite/insert/${language}`,
        {
          productCode: payload.itemArr[0].productCode
        },
        {
          headers: {
            authorization: `Bearer ${getToken()}`
          }
        }
      );
      if (res.data.success) {
        addWishGaEvent();
        yield put(
          FavoritesItemActions.postFavoritesItemSuccess(
            payload.itemArr[0].productCode
          )
        );
      } else {
        store.dispatch(ConfigActions.setShowWishIconError(true));
        if (res.data.msgCode === "UR_FAVORITE_01") {
          store.dispatch(ConfigActions.setWishMaxError(true));
        }
      }
    }
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
  } catch (error) {
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
    if (!axios.isCancel(error)) {
      const errorCode = JSON.parse(JSON.stringify(error)).status || null;
      if (errorCode && errorCode.toString() === "401") {
        yield put(ConfigActions.setShowLogin(true));
      } else {
        yield put(ConfigActions.setShowWishIconError(true));
      }
    }
  }
}

// del favorites item
export function* deleteFavoritesItemSaga(action: {
  payload: DelFavoritesItemPayload;
}) {
  try {
    const payload: DelFavoritesItemPayload = action.payload;
    yield put(FavoritesItemActions.getFavoritesItemLoading(true));
    const { region, brand_name, language } = store.getState().config;
    // remove_from_wishlist GA event
    const removeWishGaEvent = () => {
      const _price = isNotEmptyObj(payload.productInfo?.price)
        ? payload.productInfo?.price?.maxPrice ?? 0
        : 0;
      trackRemoveFromWishListEvent({
        brand: brand_name as BRAND_LIST,
        country: region as COUNTRY,
        productData: {
          ...payload.productInfo,
          displayPrice: Number(_price) ?? 0,
          currency: region === COUNTRY.TW ? "TWD" : "HKD",
          wishColorName: payload.productInfo?.color?.name || "",
          wishColorCode: payload.productInfo?.color?.displayCode || "",
          wishSize: payload.productInfo?.size || "",
          wishSizeCode: "",
          imageUrl: payload.productInfo?.pdpImageUrl || "",
          index: payload.productInfo?.index || 0
        }
      });
    };
    // GUHK
    if (brand_name === BRAND_LIST.GU && region === COUNTRY.HK) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        "/wishlist/favorite/delete",
        {
          productList: payload.itemArr
        },
        {
          headers: {
            authorization: `Bearer ${getToken()}`
          }
        }
      );
      if (res.data.success) {
        removeWishGaEvent();
        yield put(
          FavoritesItemActions.delFavoritesItemSuccess(payload.itemArr[0])
        );
      }
    }
    // UQHK -> new STG&PRD
    else if (
      brand_name === BRAND_LIST.UQ &&
      region === COUNTRY.HK &&
      !isSitOrUat()
    ) {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        "/crm/wishlist/favorite/delete",
        {
          productList: payload.itemArr
        },
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
            langCode: language
          }
        }
      );
      if (res.data.success) {
        removeWishGaEvent();
        yield put(
          FavoritesItemActions.delFavoritesItemSuccess(payload.itemArr[0])
        );
      }
    }
    // GUTW & UQTW & UQHK(SIT&UAT)
    else {
      const res: AxiosResponse<FavoritesItemResponse> = yield call(
        handRequest.post,
        `/h/hmall-favorite-service/customer/favorite/delete/${payload.itemArr[0]}/${language}`,
        {},
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
            "content-type": "application/json"
          }
        }
      );
      if (res.data.success) {
        removeWishGaEvent();
        yield put(
          FavoritesItemActions.delFavoritesItemSuccess(payload.itemArr[0])
        );
      }
    }
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
  } catch (error) {
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
    if (!axios.isCancel(error)) {
      const errorCode = JSON.parse(JSON.stringify(error)).status || null;
      if (errorCode && errorCode.toString() === "401") {
        yield put(ConfigActions.setShowLogin(true));
      } else {
        yield put(ConfigActions.setShowWishIconError(true));
      }
    }
  }
}

export const addWishlistStylingBookItem = executeOnceAtATime(
  postFavoritesItemSaga
);
export const delWishlistStylingBookItem = executeOnceAtATime(
  deleteFavoritesItemSaga
);

export function* watchLoadFavoritesItemSaga() {
  yield takeLatest<any>(GET_FAVORITES_ITEM, getFavoritesItemSaga);
  yield takeLatest<any>(POST_FAVORITES_ITEM, addWishlistStylingBookItem);
  yield takeLatest<any>(DELETE_FAVORITES_ITEM, delWishlistStylingBookItem);
}
